import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OrganisationCombobox from "@/components/pages/platform-admin/organisations/OrganisationCombobox";
import { AI_LLM_PROVIDERS, AI_VOICE_ORCHESTRATORS, AI_VOICE_PROVIDERS } from "@/model/interface/AiAgent";
import { ASSIGN_AGENT_INHERIT, AssignAgentFormController } from "@/hooks/useAssignAgentForm";

export default function AssignAgentForm({
  controller,
  lockSource = false,
}: {
  controller: AssignAgentFormController;
  lockSource?: boolean;
}) {
  const { state, setField, fixedOrganisation, templates, deployedAgents, sourceName, sourceGreeting, modelOptions } = controller;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Organisation *</Label>
        {fixedOrganisation ? (
          <p className="text-sm font-medium">{fixedOrganisation.name}</p>
        ) : (
          <div>
            <OrganisationCombobox
              value={state.pickedOrganisation}
              onChange={(organisation) => setField("pickedOrganisation", organisation)}
            />
          </div>
        )}
      </div>

      {lockSource ? (
        <div className="space-y-1.5">
          <Label>{state.sourceKind === "template" ? "Template" : "Copied from"}</Label>
          <p className="text-sm font-medium">{sourceName ?? "Not selected"}</p>
          {state.sourceKind === "agent" && (
            <p className="text-xs text-muted-foreground">
              Copies configuration only, never the source organisation's data.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Start from *</Label>
            <Select
              value={state.sourceKind}
              onValueChange={(value) => setField("sourceKind", value as "template" | "agent")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="template">Template</SelectItem>
                <SelectItem value="agent">Copy existing agent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{state.sourceKind === "template" ? "Template *" : "Source agent *"}</Label>
            {state.sourceKind === "template" ? (
              <Select value={state.templateId} onValueChange={(value) => setField("templateId", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a template" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} {template.status && template.status !== "PUBLISHED" ? `(${template.status.toLowerCase()})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={state.sourceAgentId} onValueChange={(value) => setField("sourceAgentId", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a deployed agent" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {deployedAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} · {agent.organisation?.name ?? "?"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {state.sourceKind === "agent" && (
              <p className="text-xs text-muted-foreground">
                Copies configuration only, never the source organisation's data.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Name</Label>
        <Input
          placeholder={sourceName ? `${sourceName}${state.sourceKind === "agent" ? " (copy)" : ""}` : "Inherit from source"}
          value={state.name}
          onChange={(event) => setField("name", event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Greeting</Label>
        <Textarea
          rows={2}
          placeholder={sourceGreeting ? `${sourceGreeting.slice(0, 120)}…` : "Inherit from source"}
          value={state.greeting}
          onChange={(event) => setField("greeting", event.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Orchestrator</Label>
          <Select value={state.orchestrator} onValueChange={(value) => setField("orchestrator", value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ASSIGN_AGENT_INHERIT}>Inherit from source</SelectItem>
              {AI_VOICE_ORCHESTRATORS.map((orchestrator) => (
                <SelectItem key={orchestrator.value} value={orchestrator.value}>{orchestrator.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Direction</Label>
          <Select value={state.direction} onValueChange={(value) => setField("direction", value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ASSIGN_AGENT_INHERIT}>Inherit from source</SelectItem>
              <SelectItem value="INBOUND">Inbound</SelectItem>
              <SelectItem value="OUTBOUND">Outbound</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Voice provider</Label>
          <Select value={state.voiceProvider} onValueChange={(value) => setField("voiceProvider", value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ASSIGN_AGENT_INHERIT}>Inherit from source</SelectItem>
              {AI_VOICE_PROVIDERS.map((provider) => (
                <SelectItem key={provider.value} value={provider.value}>{provider.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>LLM provider</Label>
          <Select value={state.llmProvider} onValueChange={(value) => setField("llmProvider", value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ASSIGN_AGENT_INHERIT}>Inherit from source</SelectItem>
              {AI_LLM_PROVIDERS.map((provider) => (
                <SelectItem key={provider.value} value={provider.value}>{provider.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {modelOptions.length > 0 && (
          <div className="space-y-1.5">
            <Label>LLM model</Label>
            <Select value={state.llmModel} onValueChange={(value) => setField("llmModel", value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ASSIGN_AGENT_INHERIT}>Provider default</SelectItem>
                {modelOptions.map((model) => (
                  <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
